import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import QRCode from "react-qr-code";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import {
  ArrowRight, CheckCircle2, QrCode, Zap, BarChart3,
  Globe, RefreshCw, Briefcase, Sparkles, ChevronRight,
  Building2, ShoppingBag, MapPin, Trophy
} from "lucide-react";

const HERO_PROFILES = [
  {
    imageUrl: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=900&fit=crop",
    name: "Dr. Sara Counselling",
    tagline: "Licensed Therapist & Counsellor",
    links: [
      { icon: "📞", label: "Book Session" },
      { icon: "💬", label: "Private Chat" },
      { icon: "📧", label: "Email Us" },
    ],
    initial: "D",
    placement: "translate-y-5 -rotate-6 z-0",
  },
  {
    imageUrl: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=900&fit=crop",
    name: "Maison Élégante",
    tagline: "Fine French Dining Experience",
    links: [
      { icon: "🍽", label: "View Menu" },
      { icon: "📞", label: "Reserve Table" },
      { icon: "📍", label: "Find Us" },
    ],
    initial: "M",
    placement: "z-10",
  },
  {
    imageUrl: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=600&h=900&fit=crop",
    name: "Coach Arjun Fitness",
    tagline: "Personal Trainer • Sports Coach",
    links: [
      { icon: "🏃", label: "Book Session" },
      { icon: "💬", label: "WhatsApp" },
      { icon: "📸", label: "Instagram" },
    ],
    initial: "C",
    placement: "translate-y-5 rotate-6 z-0",
  },
] as const;

function HeroProfileCards() {
  return (
    <div className="flex shrink-0 items-end justify-center -space-x-5">
      {HERO_PROFILES.map((profile) => (
        <div
          key={profile.name}
          className={`relative h-[606px] w-[280px] shrink-0 overflow-hidden rounded-[40px] bg-cover bg-center shadow-[0_8px_32px_rgba(0,0,0,0.3)] ${profile.placement}`}
          style={{ backgroundImage: `url("${profile.imageUrl}")` }}
        >
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.5)_30%,rgba(0,0,0,0.85)_100%)]" />
          <div className="absolute inset-x-0 top-[20%] z-10 px-3">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-2 border-white bg-white/20 text-sm font-bold text-white backdrop-blur-sm">
              {profile.initial}
            </div>
            <h2 className="mt-1.5 truncate text-center text-base font-bold text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]">
              {profile.name}
            </h2>
            <p className="mt-0.5 truncate text-center text-[13px] text-white/70">{profile.tagline}</p>
            <div className="mt-3 flex flex-col gap-1">
              {profile.links.map((link) => (
                <div
                  key={link.label}
                  className="flex h-[34px] w-full items-center justify-center rounded-full border border-white/25 bg-white/[0.18] px-2 text-xs font-semibold text-white backdrop-blur-[8px]"
                >
                  <span className="mr-1.5" aria-hidden="true">{link.icon}</span>
                  {link.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const [name, setName] = useState("");
  const [hoveredWho, setHoveredWho] = useState<number | null>(null);

  const previewUrl = name.trim()
    ? "https://commonqr.com/" + name.toLowerCase().replace(/\s+/g, "-")
    : "https://commonqr.com/yourname";

  const scrollToDemo = () => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });

  const WHO_ITEMS = [
    {
      title: "Brands", icon: Sparkles, slug: "brands",
      img: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=800&q=80",
      accent: "from-violet-500 to-indigo-600",
      desc: "Run campaigns, track every scan, and keep every QR always linked to your latest content across all markets."
    },
    {
      title: "Businesses", icon: Building2, slug: "businesses",
      img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
      accent: "from-indigo-500 to-sky-600",
      desc: "One QR on your door links to your menu, reviews, location, hours, and booking — updated in seconds, never reprinted."
    },
    {
      title: "Products", icon: ShoppingBag, slug: "products",
      img: "https://images.unsplash.com/photo-1491553895911-0055eca6402d?auto=format&fit=crop&w=800&q=80",
      accent: "from-rose-500 to-orange-500",
      desc: "Place your QR on packaging to link buyers to tutorials, warranty registration, reorders, and social proof."
    },
    {
      title: "Local Businesses", icon: MapPin, slug: "local-businesses",
      img: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=800&q=80",
      accent: "from-orange-500 to-amber-500",
      desc: "Restaurant? Salon? Gym? Put your QR on tables and doors. Change specials daily without reprinting a single menu."
    },
    {
      title: "Celebrities", icon: Trophy, slug: "celebrities",
      img: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=800&q=80",
      accent: "from-amber-500 to-yellow-400",
      desc: "Fans scan your QR for exclusive content, socials, merch drops, and event tickets — all in one branded profile."
    },
    {
      title: "Professionals", icon: Briefcase, slug: "professionals",
      img: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80",
      accent: "from-emerald-500 to-teal-600",
      desc: "Share your LinkedIn, portfolio, contact, and calendar in one QR on your business card. Always current."
    },
  ];

  const USE_CASES = [
    { emoji: "📇", label: "Business Cards", slug: "business-card", img: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=500&q=80", caption: "Smart card with QR — share your contact, portfolio, socials, and WhatsApp in one tap." },
    { emoji: "🧴", label: "Product Packaging", slug: "product-packaging", img: "https://images.unsplash.com/photo-1556228578-8c89e6adf883?auto=format&fit=crop&w=500&q=80", caption: "Perfume box or luxury product with QR links buyers to brand story, reorders & reviews." },
    { emoji: "🏪", label: "Storefront", slug: "storefront", img: "https://images.unsplash.com/photo-1481437156560-3205f6a55735?auto=format&fit=crop&w=500&q=80", caption: "QR standee at billing desk — smiling staff, happy customers, trust built at checkout." },
    { emoji: "🍽", label: "Restaurant Menu", slug: "restaurant-menu", img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=500&q=80", caption: "Table QR links to full digital menu — update specials daily, zero reprinting costs." },
    { emoji: "🎟", label: "Event Pass", slug: "event-pass", img: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=500&q=80", caption: "Professional event pass with QR — agenda, venue map & speaker bios in one scan." },
    { emoji: "🔗", label: "Social Hub", slug: "social-hub", img: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=500&q=80", caption: "Mobile screen links — Instagram, TikTok, YouTube, WhatsApp all in one tap." },
    { emoji: "📰", label: "Ad Campaigns", slug: "ad-campaign", img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=500&q=80", caption: "Newspaper ad with QR — readers scan to land on your full digital campaign page." },
    { emoji: "🧾", label: "Commercial Invoice", slug: "commercial-invoice", img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=500&q=80", caption: "Invoice with QR links clients to payment portal, terms, and business profile." },
    { emoji: "🛣", label: "Highway Hoarding", slug: "highway-hoarding", img: "https://images.unsplash.com/photo-1568792923760-d70635a89fdc?auto=format&fit=crop&w=500&q=80", caption: "Giant outdoor billboard with QR — drivers scan at traffic lights to reach your landing page." },
    { emoji: "🛍", label: "Retail Carry Bag", slug: "retail-carry-bag", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=500&q=80", caption: "Branded carry bag QR turns every shopper into a repeat customer — links to offers & loyalty." },
    { emoji: "✉", label: "Office Envelope", slug: "office-envelope", img: "https://images.unsplash.com/photo-1526554850534-7c78330d5f90?auto=format&fit=crop&w=500&q=80", caption: "Corporate envelope with QR — recipients scan to reach your profile, website & contact." },
    { emoji: "📦", label: "E-Commerce Parcel", slug: "ecommerce-parcel", img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=500&q=80", caption: "Parcel label with QR links buyers to unboxing tutorials, reviews & reorder offers." },
  ];

  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      <Helmet>
        <title>CommonQR — One QR. Everything Connected.</title>
        <meta name="description" content="Create your permanent QR code in 60 seconds. One QR for all your links. Works for businesses, restaurants, products, professionals." />
        <meta property="og:title" content="CommonQR — One QR. Everything Connected." />
        <meta property="og:description" content="Your permanent QR code connecting everything you are." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://commonqr.com" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Navbar />

      {/* ─────────────── HERO ─────────────── */}
      <section className="relative bg-background text-foreground min-h-[calc(100svh-57px)] flex flex-col justify-center pt-10 pb-20 lg:pt-12 lg:pb-20 overflow-hidden">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-8 relative z-10">
          <div className="grid items-stretch gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(800px,1.3fr)]">

            <div className="flex h-full max-w-xl flex-col justify-between pt-16">
              <div>
                <h1 className="text-[42px] sm:text-[52px] lg:text-[60px] font-bold leading-[1.1] tracking-[-0.03em] text-foreground mb-3">
                  The Universal QR Profile
                  <br />
                  <span className="text-primary">for Every Business.</span>
                </h1>

                <p className="text-xl font-semibold text-foreground mb-5">One QR. All Links.</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {["📱 Social Media", "📞 Contact", "📍 Location", "🎁 Deal & Offer", "💳 Payment", "🔗 Share"].map((f, i) => (
                    <span key={i} className="rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground">{f}</span>
                  ))}
                </div>

                <div className="h-px bg-border/10 mb-5" />

                <p className="text-base font-semibold text-foreground mb-3">Create Your Business QR Profile in 60 Seconds.</p>
                <div className="flex flex-col gap-2">
                  {[
                    "No Technical Skills Needed",
                    "No App Needed",
                    "Free Plan Forever",
                    "Premium Plan from $5 / Year",
                  ].map((item, i) => (
                    <span key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400 shrink-0" /> {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-3">
                <Link href="/signup" className="inline-flex items-center gap-2 px-7 py-3 bg-primary text-primary-foreground rounded-full text-sm font-semibold transition-colors shadow-xl shadow-indigo-900/40">
                  Create Free QR Profile <ArrowRight className="w-4 h-4" />
                </Link>
                <button onClick={scrollToDemo} className="inline-flex items-center gap-2 px-7 py-3 border border-border bg-transparent text-foreground hover:border-border rounded-full text-sm font-medium transition-colors">
                  See live demo
                </button>
              </div>
            </div>

            {/* Hero right — compact QR widget overlays the center card */}
            <div className="hidden h-full items-start justify-center md:flex">
              <div className="relative">
                <HeroProfileCards />

                <div className="absolute bottom-[-60px] left-1/2 z-20 w-full max-w-[180px] -translate-x-1/2 rounded-[12px] border border-white/20 bg-slate-800/95 p-2 shadow-xl backdrop-blur-[8px]">
                <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.08em] text-indigo-400">Try it live</p>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name or business"
                  className="mb-3 h-6 w-full rounded-lg border border-border/20 bg-card/10 px-2 text-[10px] text-foreground placeholder:text-muted-foreground transition-colors focus:border-indigo-500/50 focus:outline-none"
                />
                <div className="flex flex-col items-center">
                  <div className="bg-white rounded-lg p-1.5 mb-2">
                    <QRCode value={previewUrl} size={60} fgColor="#1e293b" bgColor="transparent" />
                  </div>
                  <p className="text-[9px] text-slate-500 font-mono mb-2 truncate w-full text-center">
                    commonqr.com/{name.trim().toLowerCase().replace(/\s+/g, "-") || "yourname"}
                  </p>
                  <Link href="/signup" className="flex items-center gap-0.5 text-[10px] font-medium text-indigo-400 transition-colors hover:text-indigo-300">
                    Create free <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
              </div>
            </div>

          </div>
        </div>
      </section>
      {/* ─────────────── SOCIAL PROOF BAR ─────────────── */}
      <section className="bg-card border-t border-border/10 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-2.5">
              <div className="flex -space-x-2">
                {["V","R","A","K","S"].map((l,i) => (<div key={i} className="w-8 h-8 rounded-full bg-indigo-500 border-2 border-border flex items-center justify-center text-white text-xs font-bold">{l}</div>))}
              </div>
              <span className="text-muted-foreground text-sm font-medium">Trusted by and built for <span className="text-foreground font-bold">businesses, professionals &amp; creators</span> worldwide</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.828 2.828a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/></svg>
              <span className="text-muted-foreground text-sm font-medium"><span className="text-foreground font-bold">10,000+ themes</span> across 216 industries</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <span className="text-muted-foreground text-sm font-medium"><span className="text-foreground font-bold">7 Patents</span> filed — fully protected</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              <span className="text-muted-foreground text-sm font-medium"><span className="text-foreground font-bold">60 seconds</span> to create your QR</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── LIVE DEMO ─────────────── */}
      <section id="demo" className="relative bg-background border-t border-border/10 min-h-screen flex flex-col justify-center py-16 overflow-hidden">

        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: tech messaging */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-foreground text-xs font-semibold mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                Live Preview — No Account Needed
              </div>

              <h2 className="text-3xl lg:text-[40px] font-bold text-foreground tracking-tight leading-tight mb-4">
                Your Smart QR Identity,<br />
                <span className="text-foreground">powered by intelligent automation</span>
              </h2>

              <p className="text-[17px] text-muted-foreground mb-7 leading-relaxed">
                Type your name or business below. In milliseconds, our platform generates your permanent QR profile — auto-linked, auto-tracked, always live. Zero code. Zero friction.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-7">
                {[
                  { icon: "⚡", label: "Instant generation", sub: "QR live in &lt;1 second" },
                  { icon: "🔄", label: "Real-time sync", sub: "Update without reprinting" },
                  { icon: "📊", label: "Auto analytics", sub: "Track every scan" },
                  { icon: "🔒", label: "Permanent URL", sub: "Your link, forever" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 rounded-xl border border-border bg-card p-3">
                    <span className="text-xl shrink-0">{item.icon}</span>
                    <div>
                      <div className="text-sm font-semibold text-foreground">{item.label}</div>
                      <div className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: item.sub }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1 text-foreground"><CheckCircle2 className="w-3.5 h-3.5" /> No credit card</span>
                <span className="flex items-center gap-1 text-foreground"><CheckCircle2 className="w-3.5 h-3.5" /> Free forever plan</span>
                <span className="flex items-center gap-1 text-foreground"><CheckCircle2 className="w-3.5 h-3.5" /> 60-second setup</span>
              </div>
            </div>

            {/* Right: live QR demo card */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                <span className="ml-2 text-[10px] text-muted-foreground font-mono">commonqr.com/yourname</span>
              </div>

              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-indigo-600 text-primary-foreground text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                  <label className="text-[11px] font-semibold text-foreground tracking-[0.08em] uppercase">Your name or business</label>
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Acme Coffee Co."
                  className="w-full bg-card border border-border text-foreground text-sm rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/30 placeholder:text-muted-foreground transition-all"
                />
              </div>

              <div className="flex gap-5 items-center mb-5 bg-card border border-border rounded-xl p-4">
                <div className="bg-card rounded-xl p-2.5 shrink-0 shadow-lg">
                  <QRCode value={previewUrl} size={120} fgColor="#3730a3" bgColor="transparent" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-semibold text-foreground tracking-[0.08em] uppercase mb-1.5">Your profile link</div>
                  <div className="text-sm text-foreground font-mono break-all leading-relaxed">
                    commonqr.com/<span className="text-foreground font-bold">{name.trim().toLowerCase().replace(/\s+/g, "-") || "yourname"}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 text-foreground text-xs font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Scan-ready instantly
                  </div>
                </div>
              </div>

              <Link href="/signup" className="w-full flex justify-center items-center gap-2 py-3.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-primary-foreground rounded-xl text-sm font-semibold transition-all shadow-lg shadow-indigo-900/50">
                Claim This Profile — It's Free <ArrowRight className="w-4 h-4" />
              </Link>

              <p className="text-center text-[10px] text-muted-foreground mt-3">Preview only — create an account to own this URL permanently</p>
            </div>

          </div>
        </div>
      </section>

      {/* ─────────────── USE CASES ─────────────── */}
      <section className="bg-background min-h-screen flex flex-col justify-center py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-[11px] font-semibold text-indigo-400 tracking-[0.1em] uppercase mb-3">Use Cases</p>
            <h2 className="text-3xl lg:text-[40px] font-bold text-foreground tracking-tight mb-3">One QR. Infinite applications.</h2>
            <p className="text-[17px] text-muted-foreground">Every industry, every surface — tap to see how it works.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {USE_CASES.map((uc, i) => (
              <Link key={i} href={`/${uc.slug}`} className="group relative rounded-xl overflow-hidden cursor-pointer aspect-square block">
                <img
                  src={uc.img}
                  alt={uc.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/40 to-slate-900/10 group-hover:from-slate-900/95 group-hover:via-slate-900/65 transition-all duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="text-xl mb-0.5 transition-transform duration-300 group-hover:-translate-y-1">{uc.emoji}</div>
                  <div className="text-sm font-bold text-white transition-transform duration-300 group-hover:-translate-y-1">{uc.label}</div>
                  <p className="text-[10px] text-indigo-300 leading-snug mt-1 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">{uc.caption}</p>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <span className="bg-indigo-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">Explore →</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link href="/signup" className="inline-flex items-center gap-2 px-7 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-sm font-semibold transition-colors shadow-lg shadow-indigo-900/50">
              Create My Free QR Profile <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────────── WHO IS THIS FOR ─────────────── */}
      <section className="bg-background min-h-screen flex flex-col justify-center py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-[11px] font-semibold text-indigo-600 tracking-[0.1em] uppercase mb-3">Who It's For</p>
            <h2 className="text-3xl lg:text-[40px] font-bold text-foreground tracking-tight mb-3">Built for every digital presence</h2>
            <p className="text-[17px] text-muted-foreground">From global brands to local cafés — hover to explore your fit.</p>
          </div>
        </div>

        {/* Full-width card strip */}
        <div className="w-full px-4 sm:px-6">
          <div
            className="flex gap-2 overflow-hidden rounded-2xl"
            style={{ height: "55vh" }}
          >
            {WHO_ITEMS.map((item, i) => (
              <Link
                key={i}
                href={`/${item.slug}`}
                className="relative overflow-hidden rounded-xl cursor-pointer shrink-0 block"
                style={{
                  flex: hoveredWho === null ? 1 : hoveredWho === i ? 5 : 3,
                  transition: "flex 0.45s cubic-bezier(0.4,0,0.2,1)",
                  minWidth: 0,
                }}
                onMouseEnter={() => setHoveredWho(i)}
                onMouseLeave={() => setHoveredWho(null)}
              >
                <img
                  src={item.img}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    transform: hoveredWho === i ? "scale(1.06)" : "scale(1)",
                    transition: "transform 0.5s ease",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-slate-900/10" />

                {/* Icon badge */}
                <div className={`absolute top-3 left-3 w-9 h-9 rounded-xl bg-gradient-to-br ${item.accent} flex items-center justify-center shadow-lg shrink-0`}>
                  <item.icon className="w-4.5 h-4.5 text-white" />
                </div>

                {/* Content — always visible title, description on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-sm font-bold text-white mb-1.5">{item.title}</div>
                  <p
                    className="text-xs text-muted-foreground leading-snug"
                    style={{
                      maxHeight: hoveredWho === i ? "100px" : "0px",
                      opacity: hoveredWho === i ? 1 : 0,
                      overflow: "hidden",
                      transition: "max-height 0.4s ease, opacity 0.3s ease",
                    }}
                  >
                    {item.desc}
                  </p>
                  <div
                    style={{
                      opacity: hoveredWho === i ? 1 : 0,
                      transition: "opacity 0.3s ease 0.15s",
                    }}
                  >
                    <span className={`inline-flex items-center gap-1 mt-2.5 text-[11px] font-bold text-white bg-gradient-to-r ${item.accent} px-3 py-1.5 rounded-full`}>
                      Explore <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── FEATURES (compact, 1 screen) ─────────────── */}
      <section className="bg-muted min-h-screen flex flex-col justify-center py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">

          <div className="text-center max-w-2xl mx-auto mb-3">
            <p className="text-[11px] font-semibold text-indigo-600 tracking-[0.1em] uppercase mb-3">Features</p>
            <h2 className="text-3xl lg:text-[40px] font-bold text-foreground tracking-tight mb-3">Everything your QR profile needs</h2>
          </div>

          {/* Replaces 3 tools — compact banner */}
          <div className="flex items-center gap-3 mb-8 max-w-2xl mx-auto">
            <div className="h-px bg-border flex-1" />
            <div className="flex items-center gap-2 bg-indigo-600 text-white rounded-full px-4 py-1.5 text-xs font-semibold shadow-lg shadow-indigo-200 whitespace-nowrap shrink-0">
              ✓ Replaces Linktree + QR Generators + Analytics — all in one
            </div>
            <div className="h-px bg-border flex-1" />
          </div>

          {/* 6 Feature Cards — tighter grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: Zap, bg: "bg-orange-50", color: "text-orange-500",
                img: "https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?auto=format&fit=crop&w=600&q=80",
                title: "Dynamic QR Codes", desc: "Change your links anytime. Your QR stays the same.",
                caption: "Print once. Update forever. Never lose a customer to a dead link again."
              },
              {
                icon: BarChart3, bg: "bg-emerald-50", color: "text-emerald-600",
                img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80",
                title: "Real-Time Analytics", desc: "See who scanned, when, and from where.",
                caption: "Know exactly when and where customers discover you. Data-driven decisions, instantly."
              },
              {
                icon: Globe, bg: "bg-sky-50", color: "text-sky-600",
                img: "https://images.unsplash.com/photo-1611262588024-d12430b98920?auto=format&fit=crop&w=600&q=80",
                title: "All Your Links", desc: "Social, website, location, payment — one beautiful page.",
                caption: "Instagram. WhatsApp. Google Maps. Menu. Payments. One page. One scan."
              },
              {
                icon: RefreshCw, bg: "bg-violet-50", color: "text-violet-600",
                img: "https://images.unsplash.com/photo-1586880244386-8b3e34c8382c?auto=format&fit=crop&w=600&q=80",
                title: "Instant Updates", desc: "Menu changed? Update in seconds. No reprinting.",
                caption: "New hours? New location? New offer? Live changes hit every printed QR code instantly."
              },
              {
                icon: QrCode, bg: "bg-indigo-50", color: "text-indigo-600",
                img: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=600&q=80",
                title: "Custom Profile Page", desc: "Beautiful mobile-first page at commonqr.com/you.",
                caption: "Your own professional profile page. Designed to convert visitors into customers."
              },
              {
                icon: CheckCircle2, bg: "bg-emerald-50", color: "text-emerald-600",
                img: "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=600&q=80",
                title: "Free Forever", desc: "Core features free. Upgrade only when ready.",
                caption: "Start free. No credit card. No expiry. Scale to Pro when your business demands more."
              },
            ].map((f, i) => (
              <div key={i} className="group bg-card rounded-2xl border border-border hover:border-indigo-300 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-default">
                <div className="relative overflow-hidden h-36">
                  <img
                    src={f.img}
                    alt={f.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-indigo-900/0 group-hover:bg-indigo-900/80 transition-all duration-300 flex items-center justify-center p-4">
                    <p className="text-white text-xs text-center font-medium leading-snug opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">{f.caption}</p>
                  </div>
                </div>
                <div className="p-4">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${f.bg} ${f.color} mb-2.5`}>
                    <f.icon className="w-3.5 h-3.5" />
                  </div>
                   <h3 className="text-sm font-bold text-foreground mb-1">{f.title}</h3>
                   <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── BIG STATS ─────────────── */}
      <section className="relative bg-gradient-to-br from-indigo-600 to-violet-700 py-14 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: "10,000+", label: "Professional Themes", icon: "🎨" },
              { num: "216", label: "Industries Covered", icon: "🏢" },
              { num: "7", label: "Patents Filed", icon: "🛡" },
              { num: "60 sec", label: "To Create Your QR", icon: "⚡" },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="text-2xl mb-1">{s.icon}</div>
                <div className="text-3xl font-bold text-white tracking-tight mb-0.5">{s.num}</div>
                <div className="text-sm text-indigo-200">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── PRICING (full-screen outer) ─────────────── */}
       <section id="pricing" className="bg-background min-h-screen flex flex-col justify-center py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 w-full">
          <div className="text-center mb-10">
             <p className="text-[11px] font-semibold text-primary tracking-[0.1em] uppercase mb-3">Pricing</p>
             <h2 className="text-3xl lg:text-[40px] font-bold text-foreground tracking-tight mb-3">Simple, transparent pricing</h2>
             <p className="text-[17px] text-muted-foreground">No hidden fees. No credit card to start.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">

            {/* Free Plan */}
             <div className="bg-card rounded-2xl p-7 border border-border flex flex-col">
              <span className="text-[11px] font-semibold text-emerald-600 tracking-[0.08em] uppercase mb-4 block">Free</span>
               <div className="text-4xl font-bold text-foreground tracking-tight leading-none mb-1">$0</div>
               <div className="text-sm text-muted-foreground mb-6">forever, no card needed</div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {["1 Dynamic QR Profile", "Unlimited scans", "Basic profile page", "Standard support"].map((f, i) => (
                   <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
                <Link href="/signup" className="w-full text-center border border-border bg-transparent text-foreground py-2.5 rounded-full text-sm font-medium transition-colors">
                Start Free Now
              </Link>
            </div>

            {/* Pro Plan */}
             <div className="bg-[#4F46E5] text-white rounded-2xl p-7 border border-primary flex flex-col relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-white/20 text-white text-[10px] font-semibold px-3 py-1 rounded-bl-xl tracking-wide">
                Most Popular
              </div>
              <span className="text-[11px] font-semibold text-white tracking-[0.08em] uppercase mb-4 block">Pro</span>
                <div className="text-4xl font-bold text-white tracking-tight leading-none mb-0.5">$5</div>
                <div className="text-sm text-white mb-1">per year</div>
              <div className="text-sm font-semibold text-white mb-3">10 QR Profiles</div>
              <div className="flex items-center gap-2 bg-emerald-900/40 border border-emerald-700/40 rounded-lg px-3 py-2 mb-5">
                <span className="text-lg">☕</span>
                <p className="text-xs text-white leading-snug">
                  <strong className="text-white">Just $0.50 per QR profile/year.</strong> That's less than a cup of coffee — once a year.
                </p>
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {["Unlimited Links per profile", "Advanced Analytics & Insights", "Custom Colors & Branding", "Verified Badge", "Priority Support"].map((f, i) => (
                   <li key={i} className="flex items-center gap-2 text-white">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
               <Link href="/signup" className="w-full text-center bg-white text-[#4F46E5] py-2.5 rounded-full text-sm font-semibold transition-colors">
                Get Pro — $5/Year
              </Link>
                <p className="text-center text-[10px] text-white mt-2">Less than a coffee. For an entire year.</p>
            </div>

          </div>
        </div>
      </section>

      {/* ─────────────── FINAL CTA + FOOTER (one screen) ─────────────── */}
      <section className="relative overflow-hidden" style={{ minHeight: "62vh" }}>
        {/* Background: billing counter with QR standee */}
        <img
          src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1800&q=90"
          alt="Happy customers at billing counter with QR standee"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/20 via-slate-900/55 to-indigo-900/90" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-slate-900/20" />

        {/* QR standee overlay element */}
        <div className="absolute left-8 lg:left-24 bottom-[15%] hidden lg:flex flex-col items-center gap-2 z-10">
          <div className="bg-white rounded-xl p-3 shadow-2xl border-4 border-slate-100 transform -rotate-2">
            <QRCode value="https://commonqr.com/demo" size={72} fgColor="#1e293b" bgColor="transparent" />
          </div>
          <div className="bg-indigo-600 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow-lg">Scan me!</div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 w-full py-16">
          <div className="max-w-xl ml-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-semibold mb-5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              Customer just scanned — Viewed menu, clicked WhatsApp ✓
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-[46px] font-bold leading-tight tracking-tight text-white mb-2">
              Your customers are scanning.
            </h2>
            <p className="text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight tracking-tight text-indigo-300 mb-5">
              Are you there when they do?
            </p>
            <p className="text-sm text-slate-300 max-w-md mb-5 leading-relaxed">
              When someone scans your QR, they land on your complete digital identity — links, contacts, menu, deals, and more. All in one beautiful page.
            </p>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 mb-6 space-y-2">
              <div className="text-xs font-bold text-white mb-1.5">📲 What happens when they scan your CommonQR:</div>
              {["They see all your social links instantly", "They tap to call, message, or book", "They find your location and hours", "You track every scan in real-time"].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-200">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {item}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link href="/signup" className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-indigo-700 hover:bg-indigo-50 rounded-full text-sm font-bold transition-colors shadow-xl">
                Claim My Free QR Identity <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="text-xs text-slate-400 mt-4">🔒 No spam. No credit card. Just your QR identity — free.</p>
          </div>
        </div>
      </section>

      {/* ─────────────── FOOTER ─────────────── */}
      {/* ─────────────── FAQ ─────────────── */}
       <section className="bg-background py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[11px] font-semibold text-indigo-600 tracking-[0.1em] uppercase mb-3">FAQ</p>
             <h2 className="text-3xl lg:text-[40px] font-bold text-foreground tracking-tight">Common questions</h2>
          </div>
          <div className="space-y-3">
            {[{q:"Is my QR code truly permanent?",a:"Yes. Your commonqr.com/username URL never changes. Update your profile as many times as you want — the QR stays the same forever. No reprinting ever needed."},{q:"What is available right now?",a:"Stage 1 — Universal QR Profile — is live now. Create your free permanent QR profile at commonqr.com/username. Free, Pro ($5/yr), and Pro Plus ($15/yr) plans are available."},{q:"Do I need to print a new QR if I change my details?",a:"Never. Your QR code URL is permanent. Change your phone number, address, links, or business name — the QR printed on your card or packaging always resolves to the latest version."},{q:"What happens if I do not renew my plan?",a:"Your profile enters a 90-day grace period. Your public QR and profile remain live for visitors. Your dashboard is locked until you renew. After 90 days the dashboard locks permanently until renewed."},{q:"Is CommonQR secure?",a:"Yes. Passwords are never stored in plain text — they're securely hashed. Sign-in uses secure token-based authentication, all traffic is encrypted over HTTPS, and rate limiting protects your account against brute-force attacks."},{q:"How do I get started?",a:"Click Create QR at the top of the page. Sign up in 60 seconds — your permanent QR profile is live immediately. Free plan requires no credit card."}].map((item, i) => (
               <details key={i} className="group border border-border bg-card text-foreground rounded-2xl overflow-hidden">
                 <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none hover:bg-muted transition-colors">
                   <span className="font-semibold text-foreground text-[15px]">{item.q}</span>
                   <svg className="w-5 h-5 text-muted-foreground shrink-0 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </summary>
                 <div className="px-6 pb-5 pt-1 text-muted-foreground text-[15px] leading-relaxed border-t border-border">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>
      <Footer />

    </div>
  );
}
